import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Select, Space,
  Descriptions, message, Switch, Spin
} from 'antd';
import {
  EyeOutlined, StopOutlined, CheckCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;

const AGENT_STATUS = {
  available: { color: '#52C41A', badge: 'success', label: 'Available' },
  on_delivery: { color: '#1890FF', badge: 'processing', label: 'On Delivery' },
  offline: { color: '#7A8A96', badge: 'default', label: 'Offline' },
  suspended: { color: '#FF4D4F', badge: 'error', label: 'Suspended' },
};

export default function LogisticsControl() {
  const [agents, setAgents] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentModal, setAgentModal] = useState(false);
  const [filterCity, setFilterCity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [agentsSnap, zonesSnap] = await Promise.all([
        getDocs(query(collection(db, 'deliveryAgents'), orderBy('joinedAt', 'desc'))),
        getDocs(collection(db, 'zones')),
      ]);
      setAgents(agentsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        joinedAt: d.data().joinedAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      })));
      setZones(zonesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      message.error('Failed to load logistics data');
    }
    setLoading(false);
  };

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status !== 'offline' && a.status !== 'suspended').length,
    onDelivery: agents.filter(a => a.status === 'on_delivery').length,
    activeZones: zones.filter(z => z.isActive).length,
  };

  const handleAgentStatus = async (agentId, newStatus) => {
    try {
      await updateDoc(doc(db, 'deliveryAgents', agentId), { status: newStatus, updatedAt: serverTimestamp() });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'AGENT STATUS CHANGED',
        details: 'Agent ID: ' + agentId + ' | New status: ' + newStatus,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: agentId, targetType: 'deliveryAgent',
        timestamp: serverTimestamp(),
      });
      setAgents(prev => prev.map(a => a.id === agentId ? { ...a, status: newStatus } : a));
      message.success('Agent status updated — saved to Firebase');
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleZoneToggle = async (zoneId, checked) => {
    try {
      await updateDoc(doc(db, 'zones', zoneId), { isActive: checked, updatedAt: serverTimestamp() });
      setZones(prev => prev.map(z => z.id === zoneId ? { ...z, isActive: checked } : z));
      message.success('Zone ' + (checked ? 'activated' : 'deactivated') + ' — saved to Firebase');
    } catch (error) {
      message.error('Failed to update zone');
    }
  };

  const filteredAgents = agents.filter(a => {
    const matchCity = filterCity === 'all' || a.city === filterCity;
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchCity && matchStatus;
  });

  const cities = [...new Set(agents.map(a => a.city).filter(Boolean))];

  const agentColumns = [
    {
      title: 'Agent',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.phone} • {r.zone}</div>
        </div>
      ),
    },
    {
      title: 'Vehicle',
      dataIndex: 'vehicle',
      render: (v) => <Tag style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C', border: 'none', borderRadius: 20 }}>{v}</Tag>,
    },
    {
      title: 'Performance',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ color: '#C9A84C' }}>{r.totalDeliveries} deliveries</div>
          <div style={{ color: '#7A8A96' }}>Rs.{r.totalEarnings?.toLocaleString()} earned</div>
        </div>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      render: (r) => <span style={{ color: '#FAAD14', fontWeight: 500 }}>{r} / 5.0</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => {
        const c = AGENT_STATUS[s] || AGENT_STATUS.offline;
        return <Badge status={c.badge} text={<span style={{ color: c.color, fontSize: 12 }}>{c.label}</span>} />;
      },
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelectedAgent(r); setAgentModal(true); }} />
          {r.status !== 'suspended' ? (
            <Button type="text" icon={<StopOutlined />} style={{ color: '#FF4D4F' }}
              onClick={() => handleAgentStatus(r.id, 'suspended')} />
          ) : (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => handleAgentStatus(r.id, 'available')} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Agents', value: stats.total, color: '#C9A84C' },
          { title: 'Active Agents', value: stats.active, color: '#52C41A' },
          { title: 'On Delivery', value: stats.onDelivery, color: '#1890FF' },
          { title: 'Active Zones', value: stats.activeZones, color: '#FAAD14' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value} valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ color: '#C9A84C', fontWeight: 500 }}>Delivery Agents</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select value={filterCity} onChange={setFilterCity} style={{ width: 160 }}>
              <Option value="all">All Cities</Option>
              {cities.map(c => <Option key={c} value={c}>{c}</Option>)}
            </Select>
            <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 160 }}>
              <Option value="all">All Status</Option>
              <Option value="available">Available</Option>
              <Option value="on_delivery">On Delivery</Option>
              <Option value="offline">Offline</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
            <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadData}>Refresh</Button>
          </div>
        </div>
      </Card>

      <Card style={{ ...CARD_STYLE, marginBottom: 24 }} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table dataSource={filteredAgents} columns={agentColumns} rowKey="id" pagination={{ pageSize: 10 }} />
        </Spin>
      </Card>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <span style={{ color: '#C9A84C', fontWeight: 500 }}>Zone & Serviceability Management</span>
      </Card>
      <Row gutter={[16, 16]}>
        {zones.map(zone => (
          <Col xs={24} lg={12} key={zone.id}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <div style={{ color: '#D4DCE3', fontWeight: 500 }}>{zone.name}</div>
                  <div style={{ fontSize: 11, color: '#7A8A96', marginTop: 2 }}>{zone.city} • {zone.agentCount} agents</div>
                </div>
                <Switch checked={zone.isActive} checkedChildren="Active" unCheckedChildren="Inactive"
                  onChange={(checked) => handleZoneToggle(zone.id, checked)}
                  style={{ background: zone.isActive ? '#C9A84C' : undefined }} />
              </div>
              <div style={{ fontSize: 11, color: '#7A8A96' }}>
                Pincodes: {zone.pincodes?.join(', ')}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal title={<span style={{ color: '#C9A84C' }}>Agent Profile - {selectedAgent?.name}</span>}
        open={agentModal} onCancel={() => setAgentModal(false)} width={560}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setAgentModal(false)}>Close</Button>
            {selectedAgent?.status !== 'suspended' ? (
              <Button danger onClick={() => { handleAgentStatus(selectedAgent.id, 'suspended'); setAgentModal(false); }}>Suspend Agent</Button>
            ) : (
              <Button style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => { handleAgentStatus(selectedAgent.id, 'available'); setAgentModal(false); }}>Reactivate</Button>
            )}
          </div>
        }>
        {selectedAgent && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Name">{selectedAgent.name}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedAgent.phone}</Descriptions.Item>
            <Descriptions.Item label="City">{selectedAgent.city}</Descriptions.Item>
            <Descriptions.Item label="Zone">{selectedAgent.zone}</Descriptions.Item>
            <Descriptions.Item label="Vehicle">{selectedAgent.vehicle}</Descriptions.Item>
            <Descriptions.Item label="Vehicle No.">{selectedAgent.vehicleNumber}</Descriptions.Item>
            <Descriptions.Item label="Aadhaar">{selectedAgent.aadhaar}</Descriptions.Item>
            <Descriptions.Item label="Joined">{selectedAgent.joinedAt}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={AGENT_STATUS[selectedAgent.status]?.badge}
                text={<span style={{ color: AGENT_STATUS[selectedAgent.status]?.color }}>{AGENT_STATUS[selectedAgent.status]?.label}</span>} />
            </Descriptions.Item>
            <Descriptions.Item label="Rating">{selectedAgent.rating} / 5.0</Descriptions.Item>
            <Descriptions.Item label="Deliveries">{selectedAgent.totalDeliveries}</Descriptions.Item>
            <Descriptions.Item label="Earnings">Rs.{selectedAgent.totalEarnings?.toLocaleString()}</Descriptions.Item>
            {selectedAgent.activeOrderId && (
              <Descriptions.Item label="Active Order" span={2}>
                <span style={{ color: '#C9A84C', fontFamily: 'monospace' }}>{selectedAgent.activeOrderId}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}